// Chat.tsxのロジックを実装

import { assertNotNull, escapeRegex, sleep } from '@remote-voice/utilities';
import axios from 'axios';
import Cookies from 'js-cookie';
import Enumerable from 'linq';
import { enqueueSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import useLanguage from '@/components/hooks/useLanguage';
import { AudioDeviceEventCallbacks } from '@/components/hooks/useMiniRecognition';
import { useSetOpenNetworkAlert } from '@/components/hooks/useOpenNetworkAlert';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import useSubUserLanguage, {
  useSetSubUserLanguage,
} from '@/components/hooks/useSubUserLanguage';
import { useUserName } from '@/components/hooks/useUserName';
import { ChatMessageItem } from '@/components/organisms/chat/ChatMessageCache';
import useChatMessages, {
  UpsertMessageParams,
} from '@/components/organisms/chat/useChatMessages';
import useChatMini from '@/components/organisms/chat/useChatMini';
import useChatPlayer from '@/components/organisms/chat/useChatPlayer';
import useChatUserCookieInfo from '@/components/organisms/chat/useChatUserCookieInfo';
import useChatUsers from '@/components/organisms/chat/useChatUsers';
import { useProjectionMode } from '@/components/organisms/chat/useProjectionMode';
import {
  TtsSocketNotConnectedError,
  useTtsWebSocket,
} from '@/components/organisms/chat/useTtsWebSocket';
import { ChatEnvironment } from '@/components/organisms/chatEnvironmentSettingsDialog/ChatEnvironmentSettingsDialog';
import useConfirmContinueDialog from '@/components/organisms/confirmDialog/useConfirmContinueDialog';
import useConfirmOkDialog from '@/components/organisms/confirmDialog/useConfirmOkDialog';
import { env } from '@/env';
import {
  ChatUserRole,
  useChatCommandExecutedSubscription,
  useClearChatSessionMutation,
  useEndChatSessionMutation,
  useMuteUserMicMutation,
  useRemoveChatMessageMutation,
  useSetChatReactionMutation,
  useRemoveChatSessionUserMutation,
  ChatReactionType,
  useAddChatTemplateMessageMutation,
  useAddFilesMutation,
  useChatRoomsTemplateMessagesQuery,
  useNgWordsQuery,
  useSetChatMessageFlagMutation,
  TtsService,
  useSetChatSessionQuestionModeMutation,
  useSetChatSessionVoiceModeMutation,
  useSetChatEnvironmentSettingsMutation,
} from '@/types/graphql';
import SystemCommand from '@/types/SystemCommand';
import { getFileType } from '@/utils/getFileType';
import { isChatGuest, isChatHost } from '@/utils/userRole';

const useChat = (props: {
  ttsService: TtsService;
  audioDeviceEventCallbacks?: AudioDeviceEventCallbacks;
}) => {
  const { t } = useTranslation('chat');

  // 各種パラメータをクッキーなどから取得
  const params = useParams();
  const showSnackbar = useSnackbar();
  const chatRoomId = params.chatRoomId ?? '';
  const chatSessionId = params.chatSessionId ?? '';
  const sessionEntryCode =
    Cookies.get('RV_SESSION_PASS_' + chatSessionId) ?? '';
  const { userId, userPassword } = useChatUserCookieInfo();
  const userName = useUserName();
  const language = useLanguage();
  const subUserLanguage = useSubUserLanguage();
  const setSubUserLanguage = useSetSubUserLanguage();

  const [isQuestionMode, setIsQuestionMode] = useState(false);
  const [isRemoteMode, setIsRemoteMode] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const sessionStreamIdRef = useRef<string>();
  const wsSessionId = `session_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 9)}`;
  const wsSessionIdRef = useRef<string>(wsSessionId);

  const navigate = useNavigate();
  const [reverseLanguage, setReverseLanguage] = useState<string | undefined>();
  const [isOnReverseTranslation, setIsOnReverseTransalation] = useState(true);
  const [previousMessageLoaded, setPreviousMessageLoaded] = useState(false);
  const confirmDialog = useConfirmContinueDialog();

  // 入力欄のテキスト
  const [chatText, setChatText] = useState('');
  const [isMicMode, setIsMicMode] = useState(true);
  const [isTemplateMode, setIsTemplateMode] = useState(false);
  const [isReadingAloud, setIsReadingAloud] = useState(false);

  const [playingMessageId, setPlayingMessageId] = useState<
    string | undefined
  >();
  const playingMessageCounter = useRef(0);

  // 各種ミューテーション
  const [removeChatMessage] = useRemoveChatMessageMutation();
  const [setChatReaction] = useSetChatReactionMutation();
  const [clearChatSession] = useClearChatSessionMutation();
  const [setChatSessionQuestionMode] = useSetChatSessionQuestionModeMutation();
  const [setChatSessionVoiceMode] = useSetChatSessionVoiceModeMutation();
  const [muteUserMic] = useMuteUserMicMutation();
  const [removeChatSessionUser] = useRemoveChatSessionUserMutation();
  const [endChatSession] = useEndChatSessionMutation();
  const [addChatTemplateMessage, addChatTemplateMessageResult] =
    useAddChatTemplateMessageMutation();
  const [addFiles] = useAddFilesMutation();
  const [setChatMessageFlag] = useSetChatMessageFlagMutation();
  const [setChatEnvironmentSettingsMutation] =
    useSetChatEnvironmentSettingsMutation();
  const [isHiddenInformation, setIsHiddenInformation] = useState(true);

  const confirmOkDialog = useConfirmOkDialog();

  // チャットに参加しているユーザーのリスト
  const { users, loading: usersLoading } = useChatUsers({
    variables: {
      chatSessionId,
      sessionEntryCode,
      userId,
      userPassword,
      userName,
      language,
      subUserLanguage,
    },
    onMaxUser: () => {
      showSnackbar('error', t('wating.maxUsers'));
      redirectToTop();
      return;
    },
    onRoomAccessDenied: () => {
      console.error('Room access denied.');
      showSnackbar('error', t('unloginableMsg'));
      navigate('/');
      return;
    },
  });
  const joiningUsers = users.filter(
    (x) => x.isJoining && x.isWaiting === false
  );

  // 参加リストから自分の権限を判定
  const chatUserRole = users.find((x) => x.userId === userId)?.userRole;

  const playMessageEnabledRef = useRef(false); // 自動再生の設定になっているか
  const isPlayingRef = useRef(false);
  const isAutoPlayingRef = useRef(false);
  const isAutoPlayingStartRef = useRef(false);
  const isWaitingPlaying = useRef(false); // 再生終了を待機しているのが、待つ必要があるメッセージかどうかを保持

  // 受信または待機中のメッセージをチャットリストに表示するかを計算する。
  const checkCanShowMessage = useCallback(
    (item: ChatMessageItem) => {
      const isPlayable = (item: ChatMessageItem) =>
        item.isRemoved === false && // 削除されていない
        (item.languages.find((x) => x.language === language)?.message ?? '') !==
          '' && // 送信中以外
        item.userId !== userId && // 自分以外
        item.userId !== '00000000-0000-0000-0000-000000000000'; // システムメッセージ

      // 自動再生設定でなければOK
      if (playMessageEnabledRef.current === false) {
        return true;
      }

      // 自動再生中かどうか（待機するものかどうかは関係なく）
      const isAutoPlaying =
        isAutoPlayingRef.current || isAutoPlayingStartRef.current;

      if (item.isWaitingPlaying !== true || item.userId === userId) {
        // 待機の必要がないメッセージの場合、再生中の音声も待機が必要じゃなければOK
        if (isAutoPlaying === false || isWaitingPlaying.current === false) {
          if (isPlayable(item)) {
            isAutoPlayingStartRef.current = true;
            isWaitingPlaying.current = false;
          }
          return true;
        } else {
          return false;
        }
      } else {
        // 再生終了を待機するメッセージは、手動でほかのメッセージを再生しているときに割り込んでしまって再生されないことがないようにするため、通常の再生中かどうかも判断する。
        if (isAutoPlaying === false && isPlayingRef.current === false) {
          isAutoPlayingStartRef.current = true;
          isWaitingPlaying.current = true;
          return true;
        } else {
          return false;
        }
      }
    },
    [language, userId]
  );
  const canGuideControl = isChatHost(chatUserRole);
  // チャットのメッセージリスト
  const {
    messages,
    upsertMessage: chatMessagesUpsertMessage,
    loadPreviousMessages,
    getPreviousMessage,
    getNextMessage,
    loading,
    loadingPrevious,
    canLoadPrevious,
    sessionInfo,
    isInitialized,
    // updateWaitingByForce,
  } = useChatMessages({
    chatRoomId: chatRoomId,
    chatSessionId: chatSessionId,
    sessionEntryCode: sessionEntryCode,
    userId: userId,
    userPassword: userPassword,
    language: language,
    subUserLanguage: subUserLanguage,
    reverseLanguage: reverseLanguage,
    chatUserRole: chatUserRole,
    checkCanShowMessage: checkCanShowMessage,
    isOnReverseTranslation,
    canGuideControl,
  });

  const setOpenNetworkAlert = useSetOpenNetworkAlert();

  // 送信中を外部に知らせるための関数ラッパー
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const sendingCount = useRef(0);
  const upsertMessage = useCallback(
    async (params: UpsertMessageParams) => {
      sendingCount.current++;
      setIsSendingMessage(sendingCount.current > 0);
      try {
        return await chatMessagesUpsertMessage(params);
      } finally {
        sendingCount.current--;
        setIsSendingMessage(sendingCount.current > 0);
      }
    },
    [chatMessagesUpsertMessage]
  );

  // NGワードリスト
  const ngWords = useNgWordsQuery({
    variables: {
      input: {
        chatUser: {
          chatRoomId,
          sessionEntryCode,
          userId,
          userPassword,
        },
      },
    },
  });

  const ngWordRegExp = useMemo(
    () =>
      new RegExp(
        `(${
          ngWords.data?.ngWords
            .map((nw) => escapeRegex(nw.word.toLocaleLowerCase()))
            .flat()
            .join('|') ?? ''
        })`,
        'gi'
      ),
    [ngWords.data?.ngWords]
  );

  // NGワード削除
  const replaceNgWord = useMemo(
    () => (src: string, replaceWith?: string) => {
      // NGワードが未取得であれば空文字を返す
      if (ngWords.data?.ngWords == null) return '';
      else {
        if (ngWords.data.ngWords.length === 0) return src;
        else return src.replace(ngWordRegExp, replaceWith ?? '****');
      }
    },
    [ngWordRegExp, ngWords.data?.ngWords]
  );

  // 返信のための情報
  const [replyInfo, setReplyInfo] = useState<
    | {
        id: string;
        userId: string;
        text: string;
        isSubUserSend?: boolean;
        isMine?: boolean;
      }
    | undefined
  >();
  const replyMessageId = useRef<string>();
  const replyRowText = replyInfo?.text;
  const isReplyGuestSend = replyInfo?.isSubUserSend;
  const isReplyMyMessage = replyInfo?.isMine;
  const replyText = useMemo(
    () => (replyRowText != null ? replaceNgWord(replyRowText) : undefined),
    [replaceNgWord, replyRowText]
  );
  const replyUserId = replyInfo?.userId;
  const replyUser = useMemo(
    () => users.find((x) => x.userId === replyUserId),
    [users, replyUserId]
  );

  // 音声系エンジン
  const mini = useChatMini({
    userId,
    chatRoomId,
    sessionEntryCode,
    segmentType: 'chunkAndSentence', // とりあえず固定
    sourceLanguage: language,
    subUserLanguage: subUserLanguage,
    targetLanguages: Enumerable.from(users)
      .where((x) => x.isJoining)
      .selectMany((x) => {
        const langs = [x.language];
        if (x.subUserLanguage) langs.push(x.subUserLanguage);
        return langs;
      })
      .distinct()
      // .where((x) => x !== language)
      .toArray(),
    ttsService: props.ttsService,
    audioDeviceEventCallbacks:
      sessionInfo?.isGuestMicControlLinked ||
      (isChatHost(chatUserRole) && sessionInfo?.isGuideMicControlLinked)
        ? props.audioDeviceEventCallbacks
        : undefined,
    onTextChange: setChatText,
    onUpsertMessage: (params) => {
      const result = upsertMessage({
        ...params,
        replyMessageId: params.replyMessageId ?? replyMessageId.current,
        shouldReplaceHomophone: true,
      });
      setReplyInfo(undefined);
      replyMessageId.current = undefined;
      return result;
    },
    onSendSlowError: () => {
      console.error(t('voiceSendSlow'));
      setOpenNetworkAlert(true);
    },
  });

  // 投影モード情報
  const {
    selectedProjectionLanguage,
    setSelectedProjectionLanguage,
    isProjectionMode,
    isProjectionModeAutoPlay,
  } = useProjectionMode();

  const {
    playMessageEnabled,
    setPlayMessageEnabled,
    play,
    stop,
    isPlaying,
    isAutoPlaying,
  } = useChatPlayer({
    userId,
    language: isProjectionMode ? selectedProjectionLanguage[0] : language,
    subUserLanguage,
    dualLangTranslation: !!sessionInfo?.dualLangTranslation,
    isProjectionModeAutoPlay,
    canGuideControl,
    play: async (
      text: string,
      language: string,
      messageId: string,
      voiceRate: number
      // deviceId?: string
    ) => {
      // NGワードの置換
      const replacedText = replaceNgWord(text, '');
      if (replacedText == '') return;
      playingMessageCounter.current++;
      const counter = playingMessageCounter.current;

      if (sessionStreamIdRef.current) {
        handleResetStream();
      }
      setIsReadingAloud(true);

      // 通常モード（単端末モードでない場合）のみ、音声出力中は音声入力を停止する排他制御を行う
      if (!sessionInfo?.dualLangTranslation && mini.recording) {
        //end mic
        mini.endVoiceRecognition();
        mini.setRecording(false);
        // wait mic off
        await sleep(2000);
      }
      setPlayingMessageId(messageId);
      let retryCount = 0;
      let isCanceled = false;
      while (retryCount <= 1) {
        try {
          // await mini.play(replacedText, language, voiceRate, deviceId);
          // ストリーミング再生のみになり、もはや関数の必要がないのでここで実行

          // ユーザーによる画面操作がないと再生できないのでチェック
          {
            const isCurrentlyActive = !!(navigator as any).userActivation
              ?.isActive;
            const needsUserGesture =
              ['suspended', 'interrupted'].includes(Howler.ctx?.state) &&
              !isCurrentlyActive;
            if (needsUserGesture) {
              throw new Error('User interaction is require');
            }
          }

          // 音声伝達への切替直後などは読み上げ用WebSocketが再接続中なので、接続を待ってから送る
          await waitForConnection();
          if (counter !== playingMessageCounter.current) {
            // 接続待ちの間に停止された、または別のメッセージの再生が始まった
            isCanceled = true;
            break;
          }

          const sessionId = sendRequest({
            type: props.ttsService,
            text: replacedText,
            lang: language,
            format: env.REACT_APP_TTS_FORMAT,
            rate: voiceRate,
          });

          sessionStreamIdRef.current = sessionId;

          retryCount += 2;
        } catch (e: any) {
          if (e instanceof TtsSocketNotConnectedError) {
            // 接続できないのは画面操作では解決しないので、再生許可のダイアログは出さずに失敗とする
            console.error('TTS WebSocket is not connected', e);
            showSnackbar('error', t('playFailed'));
            break;
          }
          retryCount++;
          if (retryCount === 1) {
            // 1回目のリトライ前にダイアログを表示、手動でボタンを押してもらうことで、セキュリティーを突破する
            console.log('Retry audio playback', e);
            await confirmOkDialog.open({ title: t('startVoiceReading') });
          } else {
            // 2回目以降もエラーの場合はエラー表示して抜ける
            console.error('Retried audio playback', e);
            showSnackbar('error', t('playFailed'));
            //
          }
        }
      }
      if (isCanceled) return;
      //wait stream audio by socket
      // ※ api-gatewayはTTS要求を1件ずつ処理するため、混雑時は音声が届くまで長くかかることがある。
      //   ここにタイムアウトを入れると正常な読み上げを打ち切ってしまうので入れない
      while (sessionStreamIdRef.current) {
        await sleep(10);
      }
      if (counter === playingMessageCounter.current) {
        setPlayingMessageId(undefined);
        setIsReadingAloud(false);
      }
    },
    getNextMessage,
    getPreviousMessage,
  });

  // hooksの変数を上位の定義に渡すための苦肉の策
  useEffect(() => {
    playMessageEnabledRef.current = playMessageEnabled;
  }, [playMessageEnabled]);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    isAutoPlayingRef.current = isAutoPlaying;
    if (isAutoPlaying === false) {
      isAutoPlayingStartRef.current = false;
    }
  }, [isAutoPlaying]);

  // const play = useCallback(
  //   async (text: string, language: string, messageId: string) => {
  //     // 再生終了を待機する必要のある状態であればダイアログを表示

  //     // 自動再生中かどうか（待機するものかどうかは関係なく）
  //     const isAutoPlaying =
  //       isAutoPlayingRef.current || isAutoPlayingStartRef.current;
  //     if (isAutoPlaying && isWaitingPlaying.current) {
  //       // 確認ダイアログを表示する。
  //       if (
  //         await confirmDialog.open({
  //           title: '',
  //           message:
  //             '連続したメッセージが再生されています。停止して再生しますか？',
  //         })
  //       ) {
  //         // 待機リストをまとめて表示
  //         updateWaitingByForce();
  //       } else {
  //         return;
  //       }
  //     }

  //     playMessage(text, language, messageId);
  //   },
  //   [playMessage, confirmDialog, updateWaitingByForce]
  // );

  const { audioStream, sendRequest, waitForConnection } = useTtsWebSocket({
    wsSessionId: wsSessionIdRef.current ?? '',
    sessionStreamIdRef: sessionStreamIdRef,
    setSessionStreamId: (id: string | undefined) => {
      sessionStreamIdRef.current = id;
    },
    handleResetStream: () => {
      handleResetStream();
    },
    playMessageEnabled,
  });

  const handleResetStream = useCallback(() => {
    audioStream.reset();
    sessionStreamIdRef.current = undefined;
    setPlayingMessageId(undefined);
    setIsReadingAloud(false);
  }, [audioStream]);

  const stopChat = useCallback(() => {
    // mini.stop();
    Howler.stop();
    stop();
    // 接続待ち中の再生も送信させないようにする
    playingMessageCounter.current++;

    if (sessionStreamIdRef.current) {
      handleResetStream();
    } else {
      setPlayingMessageId(undefined);
      setIsReadingAloud(false);
    }
  }, [stop, sessionStreamIdRef, handleResetStream]);

  // 音声自動再生が指定されていれば開始
  const historyLoaded = messages.length > 0 || previousMessageLoaded; // 以前のメッセージを一度は読み込んでいるか
  useEffect(() => {
    setPlayMessageEnabled(
      isInitialized &&
        historyLoaded &&
        ((sessionInfo?.isVoiceMode === true && isProjectionMode === false) ||
          isProjectionModeAutoPlay)
    );
  }, [
    sessionInfo,
    isInitialized,
    setPlayMessageEnabled,
    historyLoaded,
    isProjectionMode,
    isProjectionModeAutoPlay,
  ]);

  useEffect(() => {
    setIsQuestionMode(sessionInfo?.isQuestionMode ?? false);
    setIsVoiceMode(sessionInfo?.isVoiceMode ?? true);
  }, [sessionInfo]);

  // チャットコマンド
  useChatCommandExecutedSubscription({
    variables: {
      input: {
        chatRoomId: chatRoomId,
        sessionEntryCode: sessionEntryCode,
      },
    },
    onData: (result) => {
      const commandResult = result.data.data?.chatCommandExecuted;
      assertNotNull(commandResult);
      const command = JSON.parse(commandResult) as SystemCommand;
      // セッションからの離脱要求
      if (command.commandType === 'removeChatSessionUser') {
        if (
          (command.targetUserId == null &&
            command.userId !== userId &&
            isChatGuest(chatUserRole)) ||
          command.targetUserId === userId
        ) {
          const surveyUrl = sessionInfo?.surveyUrl;
          if (surveyUrl) {
            window.location.replace(surveyUrl);
          } else {
            navigate('/chat/thanks');
          }
        }
      } else if (command.commandType === 'changeQuestionMode') {
        setIsQuestionMode(command.isQuestionMode);
      } else if (command.commandType === 'changeVoiceMode') {
        if (isProjectionMode === false) {
          setIsVoiceMode(command.isVoiceMode);
          setPlayMessageEnabled(command.isVoiceMode);
        }
      }
    },
    onError: (error) => {
      throw error;
    },
  });

  // 自分が参加状態になっていなければ、強制退出扱いとして離脱させる
  useEffect(() => {
    const me = users.find((x) => x.userId === userId);
    if (me && me.isJoining === false) {
      navigate('/chat/thanks');
    }
  }, [users, userId, navigate]);

  const removeUser = useCallback(
    async (targetUserId?: string) => {
      await removeChatSessionUser({
        variables: {
          input: {
            chatRoomId,
            sessionEntryCode,
            userId,
            userPassword,
            targetUserId,
          },
        },
      });
    },
    [removeChatSessionUser, chatRoomId, sessionEntryCode, userId, userPassword]
  );

  const muteUserMicFunc = useCallback(
    async (targetUserId?: string) => {
      await muteUserMic({
        variables: {
          input: {
            chatRoomId,
            sessionEntryCode,
            userId,
            userPassword,
            targetUserId,
          },
        },
      });
    },
    [muteUserMic, chatRoomId, sessionEntryCode, userId, userPassword]
  );

  const clearMessages = useCallback(async () => {
    await clearChatSession({
      variables: {
        input: {
          chatRoomId,
          sessionEntryCode,
          userId,
          userPassword,
        },
      },
    });
  }, [clearChatSession, chatRoomId, sessionEntryCode, userId, userPassword]);

  const setQuestionMode = useCallback(
    async (isQuestionMode: boolean) => {
      return (
        (
          await setChatSessionQuestionMode({
            variables: {
              input: {
                chatRoomId,
                sessionEntryCode,
                userId,
                userPassword,
                isQuestionMode,
              },
            },
          })
        ).data?.setChatSessionQuestionMode ?? false
      );
    },
    [
      setChatSessionQuestionMode,
      chatRoomId,
      sessionEntryCode,
      userId,
      userPassword,
    ]
  );

  const setVoiceMode = useCallback(
    async (isVoiceMode: boolean) => {
      return (
        (
          await setChatSessionVoiceMode({
            variables: {
              input: {
                chatRoomId,
                sessionEntryCode,
                userId,
                userPassword,
                isVoiceMode,
              },
            },
          })
        ).data?.setChatSessionVoiceMode ?? false
      );
    },
    [
      setChatSessionVoiceMode,
      chatRoomId,
      sessionEntryCode,
      userId,
      userPassword,
    ]
  );

  const setReaction = useCallback(
    async (messageId: string, type: ChatReactionType) => {
      const result = await setChatReaction({
        variables: {
          input: {
            userId: userId,
            userPassword: userPassword,
            chatMessageId: messageId,
            reactionType: type,
          },
        },
      });
      assertNotNull(result.data);
    },
    [setChatReaction, userId, userPassword]
  );

  const removeMessage = useCallback(
    async (messageId: string) => {
      await removeChatMessage({
        variables: {
          input: {
            chatMessageId: messageId,
            userId: userId,
            userPassword: userPassword,
          },
        },
      });
    },
    [removeChatMessage, userId, userPassword]
  );

  const endSession = useCallback(async () => {
    await endChatSession({
      variables: {
        input: {
          chatSessionId: chatSessionId,
          sessionEntryCode: sessionEntryCode,
          userId: userId,
          userPassword: userPassword,
        },
      },
    });
  }, [endChatSession, chatSessionId, sessionEntryCode, userId, userPassword]);

  const templateMessages = useChatRoomsTemplateMessagesQuery({
    variables: {
      input: {
        chatRoomId: chatRoomId,
        chatUser: {
          chatUserId: userId,
          chatUserPassword: userPassword,
        },
        // フッターは表示中の言語しか使わないため、その言語だけ取得して軽量化する
        // （万一 language が空なら渡さず全言語取得にフォールバック）
        language: language || undefined,
      },
    },
    skip: isTemplateMode === false,
  });

  const sendTemplate = useCallback(
    async (messageId: string) => {
      await addChatTemplateMessage({
        variables: {
          input: {
            chatRoomId: chatRoomId,
            language: language,
            userId: userId,
            sessionEntryCode: sessionEntryCode,
            templateMessageId: messageId,
            userPassword: userPassword,
          },
        },
      });
      await templateMessages.refetch();
    },
    [
      addChatTemplateMessage,
      chatRoomId,
      language,
      userId,
      sessionEntryCode,
      userPassword,
      templateMessages,
    ]
  );

  const sendImages = useCallback(
    async (files: File[], isSubUserSend?: boolean) => {
      try {
        const addFilesResult = await addFiles({
          variables: {
            input: {
              files: files.map((x) => ({
                fileName: x.name,
                fileType: getFileType(x.name),
              })),
              chatUser: {
                chatRoomId: chatRoomId,
                sessionEntryCode: sessionEntryCode,
                userId: userId,
                userPassword: userPassword,
              },
            },
          },
        });
        assertNotNull(addFilesResult.data);

        // ファイルアップロード
        await Promise.all(
          addFilesResult.data.addFiles.map((x, i) =>
            axios.put(x.signedURL, files[i], {
              headers: { 'Content-Type': files[i].type },
            })
          )
        );

        await upsertMessage({
          files: addFilesResult.data.addFiles,
          isSubUserSend: !!isSubUserSend,
        });
      } catch (e) {
        console.error(e);
        enqueueSnackbar('ファイルを送信できませんでした', { variant: 'error' });
      }
    },
    [
      chatRoomId,
      sessionEntryCode,
      userId,
      userPassword,
      addFiles,
      upsertMessage,
    ]
  );

  const sendMessage = useCallback(
    (message: string, isSubUserSend?: boolean) => {
      upsertMessage({
        languages: [
          { message, language: isSubUserSend ? subUserLanguage : language },
        ],
        replyMessageId: replyInfo?.id,
        isSubUserSend,
      });
      setChatText('');
    },
    [upsertMessage, language, subUserLanguage, replyInfo?.id]
  );

  const setPlayMessageEnabledCallback = useCallback(
    (value: boolean) => {
      if (isInitialized) {
        setPlayMessageEnabled(value);
      }
    },
    [isInitialized, setPlayMessageEnabled]
  );

  // それぞれのロールに応じたトップページへのリダイレクト処理
  const redirectToTop = useCallback(() => {
    if (chatUserRole === ChatUserRole.Guide) {
      navigate(
        `/chat/${chatRoomId}/?chatUserId=${userId}&chatUserPassword=${userPassword}`
      );
    } else if (chatUserRole === ChatUserRole.TenantCompanyAdmin) {
      navigate('/admin/room');
    } else {
      navigate(`/chat/${chatRoomId}`);
    }
  }, [chatRoomId, chatUserRole, navigate, userId, userPassword]);

  const loadPreviousMessageCallback = useCallback(async () => {
    const result = await loadPreviousMessages();
    setPreviousMessageLoaded(true);
    return result;
  }, [loadPreviousMessages]);

  // メッセージのフラグセット
  const setFlag = useCallback(
    async (messageId: string, value: boolean) => {
      const result = await setChatMessageFlag({
        variables: {
          input: {
            userId: userId,
            userPassword: userPassword,
            chatMessageId: messageId,
            isMessageFlag: value,
          },
        },
      });
      assertNotNull(result.data);
    },
    [setChatMessageFlag, userId, userPassword]
  );

  const onChangeRemoteMode = useCallback(async () => {
    const resendConfirmDialog = await confirmDialog.open({
      title: '',
      message: isRemoteMode
        ? t('templateSelect.isDisableRemoteMode')
        : t('templateSelect.isEnableRemoteMode'),
    });

    if (!resendConfirmDialog) return;
    setIsRemoteMode(!isRemoteMode);
  }, [isRemoteMode, confirmDialog, t]);

  const onChangeRemoteModeDual = useCallback(async () => {
    const resendConfirmDialogDual = await confirmDialog.open({
      title: '',
      message: isRemoteMode
        ? t('templateSelect.isDisableRemoteMode')
        : t('templateSelect.isEnableRemoteMode'),
      content: isRemoteMode
        ? React.createElement('span', {
            style: { display: 'block', textAlign: 'center' },
          })
        : React.createElement(
            'span',
            { style: { display: 'block', textAlign: 'center' } },
            t('templateSelect.isEnableRemoteModeDualMicContent')
          ),
    });

    if (!resendConfirmDialogDual) return;
    setIsRemoteMode(!isRemoteMode);
  }, [isRemoteMode, confirmDialog, t]);

  const setChatEnvironmentSettings = useCallback(
    async (params: ChatEnvironment) => {
      await setChatEnvironmentSettingsMutation({
        variables: {
          input: {
            vadPresetId: params.vadPresetId,
            chatRoomId: chatRoomId,
            sessionEntryCode: sessionEntryCode,
            userId: userId,
            userPassword: userPassword,
          },
        },
      });
    },
    [
      chatRoomId,
      sessionEntryCode,
      setChatEnvironmentSettingsMutation,
      userId,
      userPassword,
    ]
  );

  return {
    userId,
    userPassword,
    chatRoomId,
    sessionEntryCode,
    language,
    subUserLanguage,
    setSubUserLanguage,
    reverseLanguage,
    setReverseLanguage,
    chatUserRole,
    sessionInfo,
    users,
    joiningUsers,
    isQuestionMode,
    isRemoteMode,
    isVoiceMode,
    isOnReverseTranslation,

    sendMessage,
    removeMessage,
    sendTemplate,
    sendImages,
    setQuestionMode,
    setVoiceMode,
    clearMessages,
    setReaction,
    removeUser,
    muteUserMic: muteUserMicFunc,
    endSession,
    setFlag,
    onChangeRemoteMode,
    onChangeRemoteModeDual,
    setIsOnReverseTransalation,
    setChatEnvironmentSettings,

    messages,
    loadPreviousMessages: loadPreviousMessageCallback,
    getPreviousMessage,

    chatText,
    setChatText,
    isMicMode,
    setIsMicMode,
    isTemplateMode,
    setIsTemplateMode,
    isReadingAloud,
    setIsReadingAloud,
    templateMessages: templateMessages.data?.chatRoomsTemplateMessages,

    playMessageEnabled,
    setPlayMessageEnabled: setPlayMessageEnabledCallback,
    playChat: play,
    stopChat,
    mini,
    playingMessageId,
    isSendingMessage,

    replaceNgWord,

    setReplyInfo: (
      value:
        | {
            id: string;
            userId: string;
            text: string;
            isSubUserSend?: boolean;
            isMine?: boolean;
          }
        | undefined
    ) => {
      setReplyInfo(value);
      replyMessageId.current = value?.id;
    },
    replyUser,
    replyText,
    isReplyGuestSend,
    isReplyMyMessage,

    loading:
      loading ||
      usersLoading ||
      templateMessages.loading ||
      addChatTemplateMessageResult.loading,

    loadingPrevious,
    canLoadPrevious,

    isHiddenInformation,
    setIsHiddenInformation,

    selectedProjectionLanguage,
    setSelectedProjectionLanguage,
    isProjectionMode,
    isProjectionModeAutoPlay,
  };
};
export default useChat;
